import { SessionKindGroupName } from 'types/models/SessionsPageData';

/**
 * Column metric keys that can appear in measurement tables.
 * These map to MeasurementNumericField types from playerPerformanceUtils.
 */
export type ColumnMetricKey =
  | 'ballSpeed'
  | 'hangTime'
  | 'distance'
  | 'gFactor'
  | 'spinRate'
  | 'spinAxis'
  | 'launchAngle'
  | 'launchDirection'
  | 'pullDirection'
  | 'maxHeight'
  | 'goalCrossingSpeed'
  | 'wallCrossingHeight';

/**
 * Configuration for which metrics are available for each session kind.
 * Defines which metrics should be displayed in table column pickers and
 * what the default column selections are.
 */
interface KindMetricConfig {
  /** Metrics available for this kind (can be selected in column picker). */
  availableMetrics: ColumnMetricKey[];
  /** Default metric columns [col0, col1, col2]. */
  defaults: [ColumnMetricKey, ColumnMetricKey, ColumnMetricKey];
}

/**
 * Kind-specific metric availability and defaults.
 * Use this to hide metrics that don't make sense for specific session types.
 *
 * Example: Penalty has distance data sparse/unreliable, so we hide it.
 */
export const KIND_METRIC_CONFIG: Record<SessionKindGroupName, KindMetricConfig> = {
  FreeKick: {
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'distance',
      'gFactor',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'pullDirection',
      'maxHeight',
      'goalCrossingSpeed',
      'wallCrossingHeight',
    ],
    defaults: ['ballSpeed', 'hangTime', 'distance'],
  },
  Penalty: {
    // Penalty distance is unreliable (short range), so hidden from picker
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'gFactor',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'pullDirection',
      'maxHeight',
      'goalCrossingSpeed',
    ],
    defaults: ['ballSpeed', 'hangTime', 'gFactor'],
  },
  Shooting: {
    // Shooting distance is unreliable (close-range shots), so hidden from picker
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'gFactor',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'pullDirection',
      'maxHeight',
      'goalCrossingSpeed',
    ],
    defaults: ['ballSpeed', 'hangTime', 'gFactor'],
  },
  Corner: {
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'distance',
      'gFactor',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'pullDirection',
      'maxHeight',
    ],
    defaults: ['ballSpeed', 'hangTime', 'distance'],
  },
  ThrowIn: {
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'distance',
      'gFactor',
      'spinRate',
    ],
    defaults: ['ballSpeed', 'hangTime', 'distance'],
  },
  Goalkeeper: {
    availableMetrics: [
      'ballSpeed',
      'hangTime',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'maxHeight',
    ],
    defaults: ['ballSpeed', 'hangTime', 'spinRate'],
  },
};

/**
 * Get available metrics for a session kind.
 * If kind is not provided, returns all metrics.
 */
export function getAvailableMetricsForKind(kind?: SessionKindGroupName): ColumnMetricKey[] {
  if (!kind) {
    // When no kind specified, include all metrics (used in non-kind-filtered contexts)
    return [
      'ballSpeed',
      'hangTime',
      'distance',
      'gFactor',
      'spinRate',
      'spinAxis',
      'launchAngle',
      'launchDirection',
      'pullDirection',
      'maxHeight',
      'goalCrossingSpeed',
      'wallCrossingHeight',
    ];
  }
  const config = KIND_METRIC_CONFIG[kind];
  return config?.availableMetrics ?? [];
}

/**
 * Get default columns for a session kind.
 */
export function getDefaultColumnsForKind(kind?: SessionKindGroupName): [ColumnMetricKey, ColumnMetricKey, ColumnMetricKey] {
  if (!kind) {
    return ['ballSpeed', 'hangTime', 'distance'];
  }
  const config = KIND_METRIC_CONFIG[kind];
  return config?.defaults ?? ['ballSpeed', 'hangTime', 'distance'];
}
